import { useAuth } from '@/auth/auth-provider'
import { canSeeData, parsePrincipalFromUser } from '@/auth/principal'
import { UserRole } from '@/config/auth.config'
import { useDealershipGroups } from '@/hooks/useDashboard'
import { Avatar, Badge, Box, Button, Container, Divider, Group, LoadingOverlay, Stack, Text, Title } from '@mantine/core'
import { IconEdit } from '@tabler/icons-react'
import { useMemo } from 'react'

/** Every account the user can see; loaded only for roles that see data, so hooks stay valid in the parent. */
function SuperAdminAccounts() {
  const { data, isLoading } = useDealershipGroups()

  return (
    <Group align="flex-start">
      <Text size="sm" c="dimmed" style={{ minWidth: 120 }}>
        Accounts
      </Text>
      {isLoading ? (
        <Text size="sm" c="dimmed">
          Loading…
        </Text>
      ) : data && data.length > 0 ? (
        <Group gap="xs">
          {data.map(group => (
            <Badge key={group.groupId} size="sm" variant="outline" color="blue">
              {group.configurationData?.groupName || group.groupId}
            </Badge>
          ))}
        </Group>
      ) : (
        <Text size="sm" c="dimmed">
          No associated accounts
        </Text>
      )}
    </Group>
  )
}

export function UserProfile() {
  const { user, isLoading } = useAuth()

  const principal = useMemo(() => parsePrincipalFromUser(user), [user])
  const seesAllAccounts = canSeeData(principal)

  if (isLoading) {
    return (
      <Box style={{ position: 'relative', minHeight: 400 }}>
        <LoadingOverlay visible={true} />
      </Box>
    )
  }

  if (!user) {
    return (
      <Container size="md" py="lg">
        <Text ta="center">No user information available</Text>
      </Container>
    )
  }

  const getRoleConfig = () => {
    switch (principal.role as UserRole) {
      case 'SUPER_ADMIN':
        return { color: 'red', label: 'Super Admin' }
      case 'STAFF':
        return { color: 'indigo', label: 'Staff' }
      default:
        return { color: 'gray', label: 'Unknown Role' }
    }
  }

  const roleConfig = getRoleConfig()

  return (
    <Container size="md" py="lg">
      <Stack gap="xl">
        {/* Header Section */}
        <Group justify="space-between" align="flex-start">
          <Group gap="md" align="center">
            <Avatar size={80} alt={user.name || 'User'}>
              {user.name?.charAt(0) || 'U'}
            </Avatar>
            <Stack gap={4}>
              <Title order={2}>{user.name || 'Unknown User'}</Title>
              <Badge size="lg" color={roleConfig.color} variant="light">
                {roleConfig.label}
              </Badge>
            </Stack>
          </Group>
          <Button
            variant="default"
            leftSection={<IconEdit size={16} />}
            onClick={() => {
              // TODO: Implement edit functionality
            }}
          >
            Edit
          </Button>
        </Group>

        <Divider />

        {/* About Section */}
        <Box>
          <Title order={4} mb="md">
            About
          </Title>
          <Stack gap="md">
            <Group>
              <Text size="sm" c="dimmed" style={{ minWidth: 120 }}>
                Name
              </Text>
              <Text size="sm" fw={500}>
                {user.name || 'Not provided'}
              </Text>
            </Group>

            <Group>
              <Text size="sm" c="dimmed" style={{ minWidth: 120 }}>
                Email
              </Text>
              <Group gap="xs">
                <Text size="sm" fw={500}>
                  {user.email || 'Not provided'}
                </Text>
                {user.email_verified && (
                  <Badge size="sm" color="green" variant="light">
                    VERIFIED
                  </Badge>
                )}
              </Group>
            </Group>

            {user.nickname && user.nickname !== user.name && (
              <Group>
                <Text size="sm" c="dimmed" style={{ minWidth: 120 }}>
                  Username
                </Text>
                <Text size="sm" fw={500}>
                  @{user.nickname}
                </Text>
              </Group>
            )}

            <Group>
              <Text size="sm" c="dimmed" style={{ minWidth: 120 }}>
                User ID
              </Text>
              <Text size="sm" fw={500} style={{ fontFamily: 'monospace' }}>
                {user.sub}
              </Text>
            </Group>

            {user.updated_at && (
              <Group>
                <Text size="sm" c="dimmed" style={{ minWidth: 120 }}>
                  Last Login
                </Text>
                <Text size="sm" fw={500}>
                  {new Date(user.updated_at).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                  {' at '}
                  {new Date(user.updated_at).toLocaleTimeString('en-US', {
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true,
                  })}
                </Text>
              </Group>
            )}
          </Stack>
        </Box>

        <Divider />

        {/* Roles & Permissions Section */}
        <Box>
          <Title order={4} mb="md">
            Roles & Permissions
          </Title>
          <Stack gap="md">
            <Group>
              <Text size="sm" c="dimmed" style={{ minWidth: 120 }}>
                Role
              </Text>
              <Badge size="md" color={roleConfig.color} variant="light">
                {roleConfig.label}
              </Badge>
            </Group>

            {principal.role && principal.role !== 'NONE' && (
              <Group>
                <Text size="sm" c="dimmed" style={{ minWidth: 120 }}>
                  Role claim
                </Text>
                <Badge size="md" color="gray" variant="outline">
                  {principal.role}
                </Badge>
              </Group>
            )}

            {seesAllAccounts && <SuperAdminAccounts />}

          </Stack>
        </Box>
      </Stack>
    </Container>
  )
}
